import {
  Component,
  OnInit,
  DestroyRef,
  inject,
  input,
  signal,
  computed,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";

import { TranslateModule } from "@ngx-translate/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { StudentMeetdayQrComponent } from "../student-meetday-qr/student-meetday-qr.component";
import { CompanyCardComponent } from "../../shared/components/network-card/company-card.component";
import { MeetDayService } from "../../api/services/meet-day.service";
import { MeetDay } from "../../api/models/meet-day";
import { MeetDayRound } from "../../api/models/meet-day-round";
import { MeetDayStudent } from "../../api/models/meet-day-student";
import { AnonymousMeetdayStudent } from "../../api/models/anonymous-meetday-student";
import { Company } from "../../api/models/company";
import { companyprofilePlaceholder } from "../../model/model.companyprofile";
import { AuthenticationService } from "../../shared/services/authentication.service";
import { filter, first, map, Observable, switchMap, tap } from "rxjs";
import { LocaleService } from "../../shared/services/locale.service";
import { StudentProfile } from "../../api/models/student-profile";
import { ModalService } from "../../shared/services/modal.service";
import { StudentProfileService } from "../../api/services/student-profile.service";
import { ModalStudentProfileResumeComponent } from "../student-profile/modal-student-profile-resume/modal-student-profile-resume.component";
import { faIdCard } from "@fortawesome/free-solid-svg-icons";
import { marker } from "@biesbjerg/ngx-translate-extract-marker";
import { RoleTypes } from "src/app/guards/roletypes";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { empty, sortArrayOnKey } from "../../functions";
import { EducationLevelService } from "../../api/services/education-level.service";
import { EducationLevel } from "../../api/models/education-level";
import { DomainService } from "src/app/shared/services/domain.service";
import { ActivatedRoute } from "@angular/router";

@Component({
  selector: "app-meetday-signup",
  templateUrl: "./meetday-signup.component.html",
  styles: [
    `
      .company-card:not(.bg-light):hover {
        border-color: var(--primary) !important;
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.15);
        transition: all 0.15s ease-in-out;
      }
    `,
  ],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    FontAwesomeModule,
    StudentMeetdayQrComponent,
    CompanyCardComponent,
  ],
})
export class MeetdaySignupComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly domainService = inject(DomainService);
  private readonly UNLIMITED_SLOTS = 999;
  faIdCard = faIdCard;

  private static readonly VALID_TARGET_GROUPS = [
    "International",
    "Professional",
  ] as const;

  public chosenCompanies: Company[] = [];
  roundOptions = signal<MeetDayRound[]>([]);
  roundSelections = signal<Record<string, string | null>>({});
  roundVisitedState = signal<Record<string, boolean>>({});
  activePanel = signal<number>(0);

  avatarPlaceholder = companyprofilePlaceholder.avatarPlaceholder;

  meetDayId = input<string | null>(null);
  meetDay = signal<MeetDay | null>(null);
  meetDayStudent = signal<MeetDayStudent | null>(null);
  public hasAcceptedPrivacyAndData = false;
  public acceptsServiceAndMarketingMessaging = false;
  isStudentLoggedIn = signal(false);
  isGuest = signal(true);
  public studentProfile: StudentProfile | null = null;
  public educationLevels: EducationLevel[] = [];

  // Computed signals
  hasRounds = computed(() => this.roundOptions().length > 0);

  registeredCompaniesSorted = computed((): Company[] => {
    const companies = this.meetDay()?.registeredCompanies || [];
    return sortArrayOnKey([...companies], "name") as Company[];
  });

  maxCompaniesSelectable = computed(() => {
    const meetDay = this.meetDay();
    if (!meetDay?.companiesPerStudent) return 0;
    const registeredCount = meetDay.registeredCompanies?.length || 0;
    return Math.min(meetDay.companiesPerStudent, registeredCount);
  });

  companySelectionPositions = computed(() => {
    const max = this.maxCompaniesSelectable();
    return Array.from({ length: max }, (_, i) => i + 1);
  });

  isCompanySelectionEnabled = computed(() => {
    const meetDay = this.meetDay();
    // Rounds mode doesn't require companiesPerStudent
    if (this.hasRounds()) {
      return (meetDay?.registeredCompanies?.length ?? 0) > 0;
    }
    // Simple mode requires companiesPerStudent
    return (
      meetDay?.companiesPerStudent != null && this.maxCompaniesSelectable() > 0
    );
  });

  isMeetDayFull = computed(() => {
    const meetDay = this.meetDay();
    const max = meetDay?.maximumAmountOfStudentsForMeetDay;
    if (max == null) return false;
    const current = meetDay?.currentlyRegisteredStudents ?? 0;
    return current >= max;
  });

  roleType = "";

  // Signals for registration state
  isSubmitting = signal<boolean>(false);

  // Signals for anonymous registration
  showAnonymousForm = signal<boolean>(false);
  isSubmittingAnonymous = signal<boolean>(false);
  anonymousFormData = signal<AnonymousMeetdayStudent>({
    firstName: "",
    lastName: "",
    emailAddress: "",
    phoneNumber: "",
    streetName: "",
    houseNumber: "",
    zipCode: "",
    city: "",
    educationalInstitution: "",
    educationLevel: "",
    academicYear: "",
    studyProgram: "",
    chosenCompanies: [],
    chosenMeetDayRounds: [],
  });

  // Separate consent fields for anonymous users
  anonymousHasAcceptedPrivacyAndData = signal<boolean>(false);
  anonymousAcceptsServiceAndMarketingMessaging = signal<boolean>(false);

  // Computed signal for anonymous form validation
  isAnonymousFormValid = computed(() => {
    const data = this.anonymousFormData();
    return (
      !empty(data.firstName) &&
      !empty(data.lastName) &&
      !empty(data.emailAddress) &&
      !empty(data.streetName) &&
      !empty(data.houseNumber) &&
      !empty(data.zipCode) &&
      !empty(data.city) &&
      !empty(data.educationalInstitution) &&
      !empty(data.educationLevel) &&
      !empty(data.academicYear) &&
      !empty(data.studyProgram) &&
      this.anonymousHasAcceptedPrivacyAndData() &&
      this.anonymousAcceptsServiceAndMarketingMessaging()
    );
  });

  constructor(
    private meetDayService: MeetDayService,
    private authService: AuthenticationService,
    public locale: LocaleService,
    public modal: ModalService,
    private route: ActivatedRoute,
    private studentProfileService: StudentProfileService,
    private educationLevelService: EducationLevelService
  ) {}

  ngOnInit(): void {
    this.loadEducationLevels();

    const resolvedId = this.meetDayId() ?? this.route.snapshot.params['id'] ?? null;

    if (resolvedId) {
      this.meetDayService
        .getMeetDay({ id: resolvedId })
        .pipe(
          switchMap((meetDay: MeetDay) => {
            this.meetDay.set(meetDay);
            this.initializeRoundsFromMeetDay(meetDay);
            return this.determineUserRole();
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe({
          next: () => {
            this.setupUserSubscriptions();
          },
          error: (error) => {
            console.error("Failed to load meetday:", error);
            this.modal.Alert(
              "Error",
              "Failed to load event details. Please try again later."
            );
          },
        });
    }
  }

  private loadEducationLevels(): void {
    const region = this.domainService.getCurrentRegion();
    this.educationLevelService
      .getEducationLevels()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.educationLevels =
          result.data
            ?.filter((educationLevel) =>
              educationLevel.regions?.includes(region)
            )
            .sort((a, b) => (a.level ?? 0) - (b.level ?? 0)) ?? [];
      });
  }

  private determineUserRole(): Observable<string> {
    return this.authService.profile$.pipe(
      first(),
      map((profile: any) => {
        const roles: string[] = profile?.roles ?? [];
        return roles.includes(RoleTypes.Student) ? RoleTypes.Student : "";
      }),
      tap((roleType: string) => {
        this.roleType = roleType;
      })
    );
  }

  private setupUserSubscriptions(): void {
    // Check authentication status
    this.authService.profile$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((profile) => {
        this.isGuest.set(profile == null);
      });

    // Handle student profile
    this.authService.studentprofile$
      .pipe(
        filter((profile) => profile != null),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((profile) => {
        this.isStudentLoggedIn.set(true);
        // Cast from ModelStudentprofile to StudentProfile - compatible fields used
        this.studentProfile = profile as unknown as StudentProfile;

        if (this.meetDay()?.id) {
          this.getMeetdayForCurrentStudent(this.meetDay()!.id!);
        }
      });
  }

  togglePanel(index: number): void {
    this.activePanel.update((current) => (current === index ? -1 : index));
  }

  getRoundSelection(roundId: string | undefined): string | null {
    if (!roundId) return null;
    return this.roundSelections()[roundId] ?? null;
  }

  setRoundSelection(
    roundId: string | undefined,
    companyId: string | null
  ): void {
    if (!roundId) return;

    // Toggle off if clicking on already-selected company
    const currentSelection = this.roundSelections()[roundId];
    const newSelection =
      currentSelection === companyId ? null : companyId || null;

    this.roundSelections.update((current) => ({
      ...current,
      [roundId]: newSelection,
    }));

    // Auto-expand next round after selection
    if (newSelection) {
      this.expandNextRound(roundId);
    }
  }

  skipRound(roundId: string | undefined): void {
    if (!roundId) return;
    // Clear the selection
    this.setRoundSelection(roundId, null);

    // Mark round as visited/skipped
    this.roundVisitedState.update((current) => ({
      ...current,
      [roundId]: true,
    }));

    // Auto-expand next round
    this.expandNextRound(roundId);
  }

  isRoundVisited(roundId: string | undefined): boolean {
    if (!roundId) return false;
    return this.roundVisitedState()[roundId] ?? false;
  }

  getRoundBadgeClass(roundId: string | undefined): string {
    if (!roundId) return "badge-primary";
    if (this.getRoundSelection(roundId)) {
      return "badge-success";
    } else if (this.isRoundVisited(roundId)) {
      return "badge-secondary";
    }
    return "badge-primary";
  }

  isCompanySelectedInOtherRound(
    companyId: string | undefined,
    currentRoundId: string | undefined
  ): boolean {
    if (!companyId || !currentRoundId) return false;
    const selections = this.roundSelections();
    return Object.entries(selections).some(
      ([roundId, selectedId]) =>
        roundId !== currentRoundId && selectedId === companyId
    );
  }

  getSelectedCompanyName(roundId: string | undefined): string | null {
    if (!roundId) return null;
    const selectedId = this.getRoundSelection(roundId);
    if (!selectedId) return null;
    const company = this.registeredCompaniesSorted().find(
      (c) => c.id === selectedId
    );
    return company?.name || null;
  }

  /**
   * Core capacity calculation - single source of truth
   */
  getCompanyCapacityInfo(
    companyId: string | undefined,
    roundId: string | undefined
  ): {
    remainingSlots: number;
    percentage: number;
    text: string;
    isFull: boolean;
  } {
    if (!companyId || !roundId) {
      return {
        remainingSlots: this.UNLIMITED_SLOTS,
        percentage: 0,
        text: "",
        isFull: false,
      };
    }

    const company = this.registeredCompaniesSorted().find(
      (c) => c.id === companyId
    );
    if (!company) {
      return {
        remainingSlots: this.UNLIMITED_SLOTS,
        percentage: 0,
        text: "",
        isFull: false,
      };
    }

    const maxCapacity =
      company.maximumStudentsForMeetDay ??
      this.meetDay()?.studentsPerMeetdayRound ??
      null;

    if (maxCapacity === null || maxCapacity === 0) {
      return {
        remainingSlots: this.UNLIMITED_SLOTS,
        percentage: 0,
        text: "",
        isFull: false,
      };
    }

    const currentRegistrations =
      company.currentStudentsPerRound?.[roundId] ?? 0;
    const remainingSlots = maxCapacity - currentRegistrations;
    const percentage = Math.min(
      100,
      Math.round((currentRegistrations / maxCapacity) * 100)
    );

    return {
      remainingSlots,
      percentage,
      text: `${currentRegistrations}/${maxCapacity}`,
      isFull: remainingSlots <= 0,
    };
  }

  isCompanyFullForRound(
    companyId: string | undefined,
    roundId: string | undefined
  ): boolean {
    const info = this.getCompanyCapacityInfo(companyId, roundId);
    return info.remainingSlots !== this.UNLIMITED_SLOTS && info.isFull;
  }

  getRoundTotalRemainingSlots(roundId: string | undefined): number {
    if (!roundId) return 0;
    const companies = this.meetDay()?.registeredCompanies || [];

    // If no companies, no slots available
    if (companies.length === 0) return 0;

    return companies.reduce((sum, company) => {
      if (!company.id) return sum;
      const remaining = this.getCompanyCapacityInfo(
        company.id,
        roundId
      ).remainingSlots;
      // Don't count unlimited slots in total
      return remaining === this.UNLIMITED_SLOTS ? sum : sum + remaining;
    }, 0);
  }

  getAvailableCompaniesForRound(roundId: string | undefined): Company[] {
    if (!roundId) return [];
    const currentSelections = this.roundSelections();
    const selectedIds = Object.entries(currentSelections)
      .filter(([key]) => key !== roundId)
      .map(([, value]) => value)
      .filter((value): value is string => !!value);

    return this.registeredCompaniesSorted().filter(
      (company) => !selectedIds.includes(company.id ?? "")
    );
  }

  getMeetdayForCurrentStudent(id: string) {
    this.meetDayService
      .getMeetDayStudentForCurrentUser({ meetDayId: id })
      .subscribe({
        next: (result: MeetDayStudent) => {
          if (result) {
            this.meetDayStudent.set(result);
            this.chosenCompanies = result.chosenCompanies || [];
            this.hydrateRoundSelections(result);
          } else {
            this.meetDayStudent.set(null);
            this.chosenCompanies = [];
            this.hydrateRoundSelections(null);
          }
        },
        error: () => {
          this.meetDayStudent.set(null);
          this.chosenCompanies = [];
          this.hydrateRoundSelections(null);
        },
      });
  }

  signUp(): void {
    if (!this.meetDay()) {
      return;
    }

    if (this.meetDay()!.id === null) {
      return;
    }

    // Prevent spam clicking
    if (this.isSubmitting()) {
      return;
    }

    if (this.isMeetDayFull()) {
      this.modal.Alert(
        marker("Registration closed"),
        marker("This meetday has reached the maximum number of registrations.")
      );
      return;
    }

    if (!this.isStudentLoggedIn()) {
      this.signUpGuest();
      return;
    }

    if (null === this.studentProfile) {
      return;
    }

    // Validate privacy consent is accepted
    if (!this.hasAcceptedPrivacyAndData) {
      this.modal.Alert(
        marker("Privacy consent required"),
        marker(
          "Please accept the privacy and data usage terms to register for this event."
        )
      );
      return;
    }

    // Set submitting state to prevent duplicate submissions
    this.isSubmitting.set(true);

    // Get companies based on mode (rounds vs simple)
    const selectedCompanies = this.hasRounds()
      ? this.composeCompaniesFromRoundSelections()
      : this.composeCompaniesFromSingleSelection();

    // Get rounds only in rounds mode
    const selectedRounds = this.hasRounds()
      ? this.getSelectedRoundsForSubmission()
      : [];

    const meetDayStudent: MeetDayStudent = {
      confirmedAttendance: false,
      studentProfileId: this.studentProfile.id,
      chosenMeetDayRounds: selectedRounds,
      chosenCompanies: selectedCompanies,
      acceptsServiceAndMarketingMessaging:
        this.acceptsServiceAndMarketingMessaging,
    };

    // Only update student profile if consent values have changed
    // The combined consent now covers GDPR, media, and data usage
    const needsProfileUpdate =
      this.studentProfile.hasMediaConsent !== this.hasAcceptedPrivacyAndData ||
      this.studentProfile.hasAcceptedGdprPolicy !==
        this.hasAcceptedPrivacyAndData ||
      this.studentProfile.hasDataUsageConsent !==
        this.hasAcceptedPrivacyAndData;

    if (needsProfileUpdate) {
      this.studentProfile.hasMediaConsent = this.hasAcceptedPrivacyAndData;
      this.studentProfile.hasAcceptedGdprPolicy =
        this.hasAcceptedPrivacyAndData;
      this.studentProfile.hasDataUsageConsent = this.hasAcceptedPrivacyAndData;

      this.studentProfileService
        .updateStudentProfile({
          id: this.studentProfile.id!,
          body: this.studentProfile,
        })
        .subscribe({
          error: (error) => {
            console.error("Failed to update student profile:", error);
          },
        });
    }

    this.meetDayService
      .createMeetDayStudent({
        meetDayId: this.meetDay()!.id!,
        body: meetDayStudent,
      })
      .subscribe({
        next: () => {
          this.isSubmitting.set(false);
          this.modal.Alert(
            marker("Event subscription"),
            marker("You are now subscribed to this event")
          );

          if (this.meetDay()?.id) {
            this.refreshMeetDay();
            this.getMeetdayForCurrentStudent(this.meetDay()!.id!);
          }
        },
        error: (error) => {
          this.isSubmitting.set(false);
          console.error("Failed to register for meetday:", error);

          const errorDetail = error.error?.detail || "";

          if (errorDetail.toLowerCase().includes("attendance cap")) {
            this.modal.Alert(
              marker("Registration closed"),
              marker(
                "This event has reached its maximum capacity. Registration is no longer available."
              )
            );
          } else if (errorDetail.toLowerCase().includes("round is full")) {
            // Round became full - show message and refresh data
            this.modal.Alert(
              marker("Selection unavailable"),
              marker(
                "One of your selected time slots is no longer available. Please make a new selection."
              )
            );
            this.refreshMeetdayAndResetSelections();
          } else {
            this.modal.Alert(
              marker("Registration Failed"),
              marker(
                "Failed to register for this event. Please try again later."
              )
            );
          }
        },
      });
  }

  signUpGuest() {
    // Mirror the landing-page register() flow so that when a guest continues
    // from the B2C login screen into "Sign up now", student-registration can
    // read the right targetGroup from localStorage and create the profile
    // accordingly. Without this it falls back to "Mty".
    const meetDayTargetGroups = this.meetDay()?.targetGroups ?? [];
    const validGroups = MeetdaySignupComponent.VALID_TARGET_GROUPS as readonly string[];
    const mappedTargetGroup =
      meetDayTargetGroups.find((g) => validGroups.includes(g)) ?? "Mty";
    this.authService.setRegistrationTargetGroup(mappedTargetGroup);

    this.authService.setRedirectUrlAfterRegistration(window.location.href);

    this.authService.Login();
  }

  unSubscribeStudentForMeetday(): void {
    if (!this.meetDay()) {
      return;
    }

    if (this.meetDay()!.id === null) {
      return;
    }

    if (!this.meetDayStudent()) {
      return;
    }

    if (this.meetDayStudent()!.id === null) {
      return;
    }

    this.meetDayService
      .deleteMeetDayStudent({
        meetDayId: this.meetDay()!.id!,
        id: this.meetDayStudent()!.id!,
      })
      .subscribe({
        next: () => {
          this.modal.Alert(
            marker("Event subscription"),
            marker("You are now unsubscribed from this event")
          );

          if (this.meetDay()?.id) {
            this.refreshMeetDay();
            this.getMeetdayForCurrentStudent(this.meetDay()!.id!);
          }
        },
        error: (error) => {
          console.error("Failed to unsubscribe from meetday:", error);
          this.modal.Alert(
            "Unsubscribe Failed",
            "Failed to unsubscribe from this event. Please try again later."
          );
        },
      });
  }

  /**
   * Companies still available for a specific selection position
   */
  getAvailableCompaniesForPosition(position: number): Company[] {
    const currentIndex = position - 1;
    const selectedIds = this.chosenCompanies
      .map((company, index) => (index !== currentIndex ? company?.id : null))
      .filter((id): id is string => !!id);

    return this.registeredCompaniesSorted().filter(
      (company) => !selectedIds.includes(company.id ?? "")
    );
  }

  /**
   * Get the company ID at a specific position (1-based index)
   */
  getCompanyIdAtPosition(position: number): string | null {
    const index = position - 1;
    return this.chosenCompanies[index]?.id || null;
  }

  /**
   * Set a company at a specific position (1-based index)
   */
  setCompanyAtPosition(position: number, companyId: string | null): void {
    if (!this.meetDay()?.registeredCompanies) return;

    const index = position - 1;
    const maxSelectable = this.maxCompaniesSelectable();

    // Don't allow positions beyond the maximum selectable
    if (position > maxSelectable) return;

    // Ensure array has enough slots up to the maximum selectable
    while (
      this.chosenCompanies.length <= index &&
      this.chosenCompanies.length < maxSelectable
    ) {
      this.chosenCompanies.push({} as Company);
    }

    if (companyId) {
      const company = this.meetDay()!.registeredCompanies!.find(
        (c) => c.id === companyId
      );
      if (company) {
        this.chosenCompanies[index] = company;
      }
    } else {
      // Clear the position
      if (index < this.chosenCompanies.length) {
        this.chosenCompanies[index] = {} as Company;
      }
    }

    // Clean up: filter out companies without IDs and maintain order
    this.chosenCompanies = this.chosenCompanies
      .map((company, i) => (i < maxSelectable && company?.id ? company : null))
      .filter((company): company is Company => company !== null)
      .slice(0, maxSelectable);
  }

  /**
   * Add resume
   */
  addResume() {
    const modalOptions = {
      backdrop: "static",
      size: "xl",
    };

    const data = Object.assign({}, this.studentProfile);

    this.modal
      .Modal(ModalStudentProfileResumeComponent, "", "", data, [], modalOptions)
      .then(() => {})
      .catch(() => {});
  }

  /**
   * Toggle anonymous registration form visibility
   */
  toggleAnonymousForm(): void {
    this.showAnonymousForm.update((value) => !value);
  }

  /**
   * Update anonymous form field
   */
  updateAnonymousFormField(
    field: keyof AnonymousMeetdayStudent,
    value: string | boolean
  ): void {
    this.anonymousFormData.update((data) => ({
      ...data,
      [field]: value,
    }));
  }

  /**
   * Update anonymous consent field
   */
  updateAnonymousConsentField(
    field: "hasAcceptedPrivacyAndData" | "acceptsServiceAndMarketingMessaging",
    value: boolean
  ): void {
    switch (field) {
      case "hasAcceptedPrivacyAndData":
        this.anonymousHasAcceptedPrivacyAndData.set(value);
        break;
      case "acceptsServiceAndMarketingMessaging":
        this.anonymousAcceptsServiceAndMarketingMessaging.set(value);
        break;
    }
  }

  /**
   * Submit anonymous registration
   */
  submitAnonymousRegistration(): void {
    if (!this.isAnonymousFormValid() || !this.meetDay()?.id) {
      return;
    }

    if (this.isMeetDayFull()) {
      this.modal.Alert(
        marker("Registration closed"),
        marker("This meetday has reached the maximum number of registrations.")
      );
      return;
    }

    this.isSubmittingAnonymous.set(true);

    this.registerAnonymousForMeetday();
  }

  /**
   * Register anonymous student for meetday using API
   */
  private registerAnonymousForMeetday(): void {
    const formData = this.anonymousFormData();

    // Get companies based on mode (rounds vs simple)
    const selectedCompanies = this.hasRounds()
      ? this.composeCompaniesFromRoundSelections()
      : this.composeCompaniesFromSingleSelection();

    // Get rounds only in rounds mode
    const selectedRounds = this.hasRounds()
      ? this.getSelectedRoundsForSubmission()
      : [];

    // Include consent fields in the submission data
    // The combined consent covers GDPR, media, and data usage
    const submissionData: AnonymousMeetdayStudent = {
      ...formData,
      chosenMeetDayRounds: selectedRounds,
      chosenCompanies: selectedCompanies,
      hasAcceptedGdprPolicy: this.anonymousHasAcceptedPrivacyAndData(),
      hasMediaConsent: this.anonymousHasAcceptedPrivacyAndData(),
      hasDataUsageConsent: this.anonymousHasAcceptedPrivacyAndData(),
      acceptsServiceAndMarketingMessaging:
        this.anonymousAcceptsServiceAndMarketingMessaging(),
    };

    // Use the anonymous registration endpoint which doesn't require authentication
    this.meetDayService
      .createAnonymousMeetDayStudent({
        meetDayId: this.meetDay()!.id!,
        body: submissionData,
      })
      .subscribe({
        next: (result: MeetDayStudent) => {
          this.isSubmittingAnonymous.set(false);
          this.modal.Alert(
            marker("Event registration successful"),
            marker("You have been successfully registered for this event.")
          );

          // Hide form after successful registration
          this.showAnonymousForm.set(false);

          // Reset form
          this.resetAnonymousForm();

          // Update meetDayStudent with result if needed
          this.meetDayStudent.set(result);
          this.refreshMeetDay();
        },
        error: (error) => {
          this.isSubmittingAnonymous.set(false);
          this.modal.Alert(
            marker("Registration failed"),
            marker(
              "There was an error processing your registration. Please try again."
            )
          );
          console.error("Anonymous registration failed:", error);
        },
      });
  }

  /**
   * Reset anonymous form data
   */
  private resetAnonymousForm(): void {
    this.anonymousFormData.set({
      firstName: "",
      lastName: "",
      emailAddress: "",
      phoneNumber: "",
      streetName: "",
      houseNumber: "",
      zipCode: "",
      city: "",
      educationalInstitution: "",
      educationLevel: "",
      academicYear: "",
      studyProgram: "",
      chosenCompanies: [],
      chosenMeetDayRounds: [],
    });

    // Reset consent fields
    this.anonymousHasAcceptedPrivacyAndData.set(false);
    this.anonymousAcceptsServiceAndMarketingMessaging.set(false);
  }

  private getSelectedRoundsForSubmission(): {
    meetDayRoundId?: string;
    companyId?: string;
  }[] {
    const selections = this.roundSelections();
    const rounds = this.roundOptions();

    const roundSelections = rounds
      .map((round) => {
        if (!round.id) return null;
        const companyId = selections[round.id];
        if (!companyId) {
          return null;
        }
        // Include both meetDayRoundId and companyId
        return { meetDayRoundId: round.id, companyId };
      })
      .filter(
        (
          selection
        ): selection is {
          meetDayRoundId: string;
          companyId: string;
        } => !!selection
      );

    return roundSelections;
  }

  private composeCompaniesFromRoundSelections(): Company[] {
    const selections = this.roundSelections();
    const rounds = this.roundOptions();
    const companies = this.meetDay()?.registeredCompanies || [];

    return rounds
      .map((round) => {
        if (!round.id) return null;
        const companyId = selections[round.id];
        if (!companyId) {
          return null;
        }
        return companies.find((company) => company.id === companyId) || null;
      })
      .filter((company): company is Company => company !== null);
  }

  private composeCompaniesFromSingleSelection(): Company[] {
    return (this.chosenCompanies || []).filter(
      (company) => !!company && !!company.id
    );
  }

  private initializeRoundsFromMeetDay(meetDay: MeetDay): void {
    const rounds = [...(meetDay.meetDayRounds || [])].sort(
      (a, b) =>
        new Date(a.roundTimeStart || "").getTime() -
        new Date(b.roundTimeStart || "").getTime()
    );

    this.roundOptions.set(rounds);
    const selections = rounds.reduce(
      (acc, round) => ({ ...acc, [round.id || ""]: null }),
      {} as Record<string, string | null>
    );
    this.roundSelections.set(selections);

    if (this.meetDayStudent()) {
      this.hydrateRoundSelections(this.meetDayStudent());
    }
  }

  private hydrateRoundSelections(student: MeetDayStudent | null): void {
    if (!this.hasRounds()) {
      return;
    }

    const rounds = this.roundOptions();
    const currentSelections = { ...this.roundSelections() };

    if (student?.chosenMeetDayRounds?.length) {
      student.chosenMeetDayRounds.forEach((roundSelection) => {
        if (!roundSelection.meetDayRoundId) {
          return;
        }
        currentSelections[roundSelection.meetDayRoundId] =
          roundSelection.companyId ?? null;
      });
    } else if (student?.chosenCompanies?.length) {
      rounds.forEach((round, index) => {
        if (!round.id) return;
        currentSelections[round.id] =
          student.chosenCompanies?.[index]?.id ?? null;
      });
    } else {
      rounds.forEach((round) => {
        if (!round.id) return;
        currentSelections[round.id] = null;
      });
    }

    this.roundSelections.set(currentSelections);
  }

  private expandNextRound(currentRoundId: string | undefined): void {
    if (!currentRoundId) return;
    const currentIndex = this.roundOptions().findIndex(
      (r) => r.id === currentRoundId
    );
    const nextIndex = currentIndex + 1;
    if (nextIndex < this.roundOptions().length) {
      this.activePanel.set(nextIndex);
    }
  }

  private refreshMeetDay(): void {
    if (!this.meetDay()?.id) return;
    this.meetDayService.getMeetDay({ id: this.meetDay()!.id! }).subscribe({
      next: (meetDay) => this.meetDay.set(meetDay),
      error: (err) => console.error("Failed to refresh meetday:", err),
    });
  }

  private refreshMeetdayAndResetSelections(): void {
    if (!this.meetDay()?.id) return;

    const meetDayId = this.meetDay()!.id!;

    // Reload meetday data to get fresh capacity info
    this.meetDayService.getMeetDay({ id: meetDayId }).subscribe({
      next: (meetDay) => {
        this.meetDay.set(meetDay);
        this.initializeRoundsFromMeetDay(meetDay);
      },
      error: (err) => console.error("Failed to refresh meetday:", err),
    });
  }
}
